// src/pages/Settings/Settings.js
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { SettingsContext } from '../../contexts/SettingsContext'; 
import SecuritySettings from './SecuritySettings';
import SupportForm from './SupportForm';
import { NetworkContext } from '../../contexts/NetworkContext';
import { formatUnits } from 'ethers';
import { formatGasPriceForDisplay } from '../../pages/Wallet/utils/gasUtils';
import { useTranslation } from 'react-i18next';
import './Settings.css';

// Import icons
import { 
  FiGlobe, 
  FiZap, 
  FiSliders, 
  FiAlertCircle,
  FiCheck,
  FiRefreshCw,
  FiShield,
  FiSettings,
  FiHelpCircle,
  FiSun, 
  FiMoon, 
  FiDownload 
} from 'react-icons/fi';

const Settings = () => {
  const { 
    language, setLanguage, 
    gasOption, setGasOption, 
    customGasPrice, setCustomGasPrice,
    autoConfirm, setAutoConfirm, 
    slippage, setSlippage,
    deadline, setDeadline,
    theme, toggleTheme,
    resetSettings,
    AVAILABLE_LANGUAGES
  } = useContext(SettingsContext);

  const { provider, currentChain } = useContext(NetworkContext);
  const { t } = useTranslation();

  const [dynamicGasPrice, setDynamicGasPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const navigate = useNavigate(); 
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  useEffect(() => {
    // Check if the app is running in standalone mode (installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    
    setIsAppInstalled(isStandalone);
  }, []);

  // Fetch network gas price on component mount and when provider changes
  useEffect(() => {
    async function fetchFeeData() {
      if (!provider) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        const feeData = await provider.getFeeData();
        const gasPriceBN = feeData.maxFeePerGas || feeData.gasPrice;
        
        if (gasPriceBN) {
          const gweiValue = formatUnits(gasPriceBN, 'gwei');
          setDynamicGasPrice(gweiValue);
        }
      } catch (err) {
        console.error("Error fetching fee data:", err);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchFeeData();
  }, [provider]);

  // Calculate suggested gas prices
  const slowGas = dynamicGasPrice ? (parseFloat(dynamicGasPrice) * 0.8).toString() : '';
  const mediumGas = dynamicGasPrice ? parseFloat(dynamicGasPrice).toString() : '';
  const fastGas = dynamicGasPrice ? (parseFloat(dynamicGasPrice) * 1.2).toString() : '';

  // Update customGasPrice when user switches gas option
  useEffect(() => {
    if (!dynamicGasPrice) return;
    
    if (gasOption === 'slow') {
      setCustomGasPrice(slowGas);
    } else if (gasOption === 'medium') {
      setCustomGasPrice(mediumGas);
    } else if (gasOption === 'fast') {
      setCustomGasPrice(fastGas);
    }
    // Don't override custom input when 'custom' is selected
  }, [gasOption, dynamicGasPrice, slowGas, mediumGas, fastGas]);

  const handleResetSettings = () => {
    setIsResetting(true);
    resetSettings();
    setTimeout(() => {
      setIsResetting(false);
    }, 1000);
  };

  // Tab switching handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="settings-container">
      <h2>{t('settings.title')}</h2>

      {/* Settings Tabs */}
      <div className="settings-tabs">
        <button 
          className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => handleTabChange('general')}
        >
          <FiSettings /> {t('settings.tabs.general')}
        </button>
        <button 
          className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => handleTabChange('security')}
        >
          <FiShield /> {t('settings.tabs.security')}
        </button>
        <button 
          className={`settings-tab ${activeTab === 'support' ? 'active' : ''}`}
          onClick={() => handleTabChange('support')}
        >
          <FiHelpCircle /> {t('settings.tabs.support')}
        </button>
      </div>

      {/* General Settings Content */}
      {activeTab === 'general' && (
        <div className="settings-tab-content">
          <h2 className="settings-section-title">{t('settings.general.title')}</h2>
          
          {/* Language Settings Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3><FiGlobe /> {t('settings.language.title')}</h3>
            </div>
            <div className="settings-card-content">
              <p className="settings-description">
                {t('settings.language.tip')}
              </p>
              
              <div className="settings-field">
                <label htmlFor="language-select">{t('settings.language.displayLanguage')}</label>
                <select
                  id="language-select"
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  className="settings-select"
                >
                  {AVAILABLE_LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Theme Settings Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3>{theme === 'dark' ? <FiMoon /> : <FiSun />} {t('settings.theme.title')}</h3>
            </div>
            <div className="settings-card-content">
              <p className="settings-description">
                {t('settings.theme.tip')}
              </p>
              
              <div className="settings-field">
                <label className="checkbox-label">
                  <span>{t('settings.theme.darkMode')}</span>
                  <div className="settings-custom-checkbox">
                    <input
                      type="checkbox"
                      checked={theme === 'dark'}
                      onChange={toggleTheme}
                    />
                    <span className="checkbox-toggle"></span>
                  </div>
                </label>
                <div className="settings-tip">
                  {theme === 'dark' 
                    ? t('settings.theme.lightModeTip') 
                    : t('settings.theme.darkModeTip')}
                </div>
              </div>
            </div>
          </div>

          {/* PWA Installation Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3><FiDownload /> {t('settings.appInstallation')}</h3>
            </div>
            <div className="settings-card-content">
              <p className="settings-description">
                {isAppInstalled 
                  ? t('settings.appInstalledDesc')
                  : t('settings.installAppDesc')}
              </p>
              
              {!isAppInstalled && (
                <div className="settings-field">
                  <div className="settings-feature-list">
                    <div className="settings-feature-item">
                      <FiCheck className="feature-icon" />
                      <span>{t('settings.pwaFeature1')}</span>
                    </div>

                    <div className="settings-feature-item">
                      <FiCheck className="feature-icon" />
                      <span>{t('settings.pwaFeature3')}</span>
                    </div>
                  </div>

                  <button 
                    className="settings-button primary"
                    onClick={() => navigate('/settings/install-guide')}
                  >
                    <FiDownload className="button-icon" />
                    {t('settings.viewInstallGuide')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Network Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3><FiCheck /> {t('settings.network.title')}</h3>
            </div>
            <div className="settings-card-content">
              <p className="settings-description">
                {t('settings.network.tip')}
              </p>
              
              <div className="settings-field highlight-field">
                <div className="network-status">
                  {t('settings.network.connected')}: <span className="network-name">{currentChain?.name || t('transactions.unknown')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Gas Settings Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3><FiZap /> {t('settings.gas.title')}</h3>
            </div>
            <div className="settings-card-content">
              <p className="settings-description">
                {t('settings.gas.gasPriceInfo')}
              </p>
              
              <div className="settings-field">
                <label htmlFor="gas-option-select">{t('settings.gas.transactionSpeed')}</label>
                <select
                  id="gas-option-select"
                  value={gasOption}
                  onChange={e => setGasOption(e.target.value)}
                  className="settings-select"
                >
                  <option value="slow">{t('settings.gas.options.slow')}</option>
                  <option value="medium">{t('settings.gas.options.medium')}</option>
                  <option value="fast">{t('settings.gas.options.fast')}</option>
                  <option value="custom">{t('settings.gas.options.custom')}</option>
                </select>
              </div>

              {/* Gas Price Information */}
              {isLoading ? (
                <div className="settings-loading">
                  <div className="loading-spinner"></div>
                  <p>{t('settings.gas.loading')}</p>
                </div>
              ) : dynamicGasPrice ? (
                <>
                  <div className="gas-price-info">
                  <div className="gas-price-current">
                    {t('settings.gas.currentGasPrice')}: <span className="gas-value">
                      {formatGasPriceForDisplay(dynamicGasPrice)} {t('settings.gas.gwei')}
                    </span>
                  </div>
                    
                  <div className="gas-price-options">
                    <div className="gas-price-option slow">
                      <div className="speed-label">{t('settings.gas.speedLabels.slow')}</div>
                      <div className="price-value">{formatGasPriceForDisplay(slowGas)} {t('settings.gas.gwei')}</div>
                    </div>
                    <div className="gas-price-option medium">
                      <div className="speed-label">{t('settings.gas.speedLabels.medium')}</div>
                      <div className="price-value">{formatGasPriceForDisplay(mediumGas)} {t('settings.gas.gwei')}</div>
                    </div>
                    <div className="gas-price-option fast">
                      <div className="speed-label">{t('settings.gas.speedLabels.fast')}</div>
                      <div className="price-value">{formatGasPriceForDisplay(fastGas)} {t('settings.gas.gwei')}</div>
                    </div>
                  </div>
                  </div>
                </>
              ) : (
                <div className="settings-alert">
                  <p>{t('settings.gas.unavailable', { network: currentChain?.name || t('settings.network') })}</p>
                </div>
              )}

              {/* Custom Gas Input */}
              {gasOption === 'custom' ? (
                <div className="settings-field">
                  <label htmlFor="custom-gas-price">{t('settings.gas.customGasPrice')}</label>
                  <div className="input-with-unit">
                    <input
                      id="custom-gas-price"
                      type="number"
                      value={customGasPrice || ''}
                      onChange={e => setCustomGasPrice(e.target.value)}
                      min="0.1"
                      step="0.1"
                      className="settings-input"
                    />
                    <span className="unit-label">{t('settings.gas.gwei')}</span>
                  </div>
                  <div className="settings-tip">
                    {t('settings.gas.customGasTip')}
                  </div>
                </div>
              ) : (
                <div className="settings-field">
                  <div className="current-setting-value">
                    {t('settings.gas.currentSetting')}: <span className="setting-value-highlight">{customGasPrice} {t('settings.gas.gwei')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Transaction Settings Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3><FiSliders /> {t('settings.transaction.title')}</h3>
            </div>
            <div className="settings-card-content">
              {/* Auto Confirm Toggle */}
              <div className="settings-field">
                <label className="checkbox-label">
                  <span>{t('settings.transaction.autoConfirm')}</span>
                  <div className="settings-custom-checkbox">
                    <input
                      type="checkbox"
                      checked={autoConfirm}
                      onChange={() => setAutoConfirm(!autoConfirm)}
                    />
                    <span className="checkbox-toggle"></span>
                  </div>
                </label>
                <div className="settings-tip">
                  {t('settings.transaction.autoConfirmTip')}
                </div>
              </div>

              {/* Slippage Setting */}
              <div className="settings-field">
                <label htmlFor="slippage-input">{t('settings.transaction.slippage')}</label>
                <div className="input-with-unit">
                  <input
                    id="slippage-input"
                    type="number"
                    value={slippage}
                    min="0.1"
                    max="49"
                    step="0.1"
                    onChange={(e) => {
                      // Allow empty value during typing
                      setSlippage(e.target.value);
                    }}
                    onBlur={() => {
                      // Validate and set default when input loses focus
                      let val = parseFloat(slippage);
                      if (isNaN(val) || val < 0.1) val = 0.5;
                      if (val > 49) val = 49;
                      setSlippage(String(val));
                    }}
                    className="settings-input"
                  />
                  <span className="unit-label">%</span>
                </div>
                {parseFloat(slippage) > 5 && (
                  <div className="settings-alert">
                    <FiAlertCircle style={{ marginRight: '5px' }} />
                    {t('settings.transaction.slippageWarning')}
                  </div>
                )}
                <div className="settings-tip">
                  {t('settings.transaction.slippageTip')}
                </div>
              </div>
              
              {/* Deadline Setting */}
              <div className="settings-field">
                <label htmlFor="deadline-input">{t('settings.transaction.deadline')}</label>
                <div className="input-with-unit">
                  <input
                    id="deadline-input"
                    type="number"
                    value={deadline}
                    min="1"
                    max="60"
                    step="1"
                    onChange={(e) => {
                      let val = parseFloat(e.target.value);
                      if (isNaN(val)) val = 20; // Default to 20 minutes
                      if (val < 1) val = 1;     // Minimum 1 minute
                      if (val > 60) val = 60;   // Maximum 60 minutes
                      setDeadline(String(val));
                    }}
                    className="settings-input"
                  />
                  <span className="unit-label">{t('settings.transaction.minutes')}</span>
                </div>
                <div className="settings-tip">
                  {t('settings.transaction.deadlineTip')}
                </div>
              </div>
            </div>
          </div>
          
          {/* Reset Settings Button */}
          <div className="settings-card">
            <div className="settings-card-header">
              <h3>{t('settings.reset.title')}</h3>
            </div>
            <div className="settings-card-content">
              <p className="settings-description">
                {t('settings.reset.description')}
              </p>
              
              <div className="settings-actions">
                <button 
                  className="settings-button danger"
                  onClick={handleResetSettings}
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <>
                      <div className="button-spinner"></div>
                      {t('settings.resetting')}
                    </>
                  ) : (
                    <>
                      <FiRefreshCw className="button-icon" />
                      {t('settings.resetSettings')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Settings Content */}
      {activeTab === 'security' && (
        <div className="settings-tab-content">
          <SecuritySettings />
        </div>
      )}
      
      {/* Support Tab Content */}
      {activeTab === 'support' && (
        <div className="settings-tab-content">
          <h2 className="settings-section-title">{t('settings.support.title')}</h2>
          <SupportForm />
        </div>
      )}
      
      {/* Add bottom spacer for mobile */}
      <div className="bottom-spacer"></div>
    </div>
  );
};

export default Settings;